import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { usePostApi } from "../../../hooks/post_api_hook/usePostApi";
import { url } from "../../../utils/confserveur";
import { GraduationCap, Users, CalendarDays, Eye, EyeOff } from 'lucide-react';
import SequencesCommunes from "./SequencesCommunes";
import SequenceCardConsultation, { calculateAcquisitionStats } from "./SequenceCardConsultation";
import ActivityListModal from "../ProgressionNiveau/ActivityListModal";
import RailAutomatismes from "../ProgressionNiveau/RailAutomatismes";
import BandeauAutomatismes from "../ProgressionNiveau/BandeauAutomatismes";
import PanneauContenus from "../ProgressionNiveau/PanneauContenus";
import VacancesSeparator from "../ProgressionNiveau/VacancesSeparator";
import { fetchSchoolHolidays } from "../../../utils/vacances";
import { useTheme } from "../../../hooks/fetch_api_hook/useTheme";
import { useUser } from "../../../context/UserContext";
import { useClasseContext } from '../../../context/ClasseContext';

// Composant principal RoadMapClasse
function RoadMapClasse({
  filteredSequences,
  selectedNiveau,
  classeNom,
  selectedView,
  onChangeView,
  // Props pour SuiviProgressionProf
  classes,
  listSequences,
  profClassIds,
  onToggleAccompli,
  handleReload
}) {
  // Hook pour l'API de mise à jour (le spinner par carte passe par
  // updatingSequenceId, pas par l'isLoading global du hook)
  const [, doPost] = usePostApi(url + "sequences/add_sequence_prof/");
  const [updatingSequenceId, setUpdatingSequenceId] = useState(null);

  // State pour le modal de liste des activités
  const [isActivityListModalOpen, setIsActivityListModalOpen] = useState(false);
  const [activityListModalData, setActivityListModalData] = useState({ type: null, activities: [], sequenceTitle: '' });


     const {classeDetail } = useClasseContext();
  const { user } = useUser();

  // Récupérer classeId en matchant classeNom avec le tableau classes
  const classeId = useMemo(() => {
    if (!classes || !classeNom) return null;
    const classe = classes.find(c => c.nom === classeNom);
    return classe?.id || null;
  }, [classes, classeNom]);

  // Vacances scolaires + périodes : même mécanique que RoadMapNiveau — la
  // frise par périodes (rails de consultation) n'existe que si le fetch
  // aboutit, sinon repli intégral sur la frise historique.
  const [vacancesScolaires, setVacancesScolaires] = useState([]);
  const [periodes, setPeriodes] = useState([]);
  useEffect(() => {
    let ignore = false;
    (async () => {
      const { vacances: vac, periode, erreur } = await fetchSchoolHolidays();
      if (ignore || erreur) return;
      if (Array.isArray(vac)) setVacancesScolaires(vac);
      if (Array.isArray(periode)) setPeriodes(periode);
    })();
    return () => { ignore = true; };
  }, []);

  // Même gate que côté niveau : pas de rails pour le français.
  const railActif = periodes.length > 0 && user?.matiere !== 'Français';

  // Panneau détail partagé (pill d'une séquence chargée / chip d'un fil),
  // en CONSULTATION : pas d'édition des savoirs depuis la classe.
  const [contenusActifs, setContenusActifs] = useState(null);

  // Rubrique de chaque attendu du référentiel (résolue pour le panneau,
  // comme côté niveau : attendus_details ne la porte pas toujours).
  const { data: themes } = useTheme(selectedNiveau?.[0]);
  const rubriqueParAttendu = useMemo(() => {
    const map = new Map();
    (Array.isArray(themes) ? themes : []).forEach((t) =>
      (t.descripteur || []).forEach((d) =>
        (d.attendus || []).forEach((att) => map.set(att.id, att.rubrique))));
    return map;
  }, [themes]);

  // Filtrer uniquement les séquences (pas les activités parallèles)
  const sequences = useMemo(() => {
    if (!filteredSequences) return [];
    return filteredSequences
      .filter(seq => seq.type === 'SEQUENCE')
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [filteredSequences]);

  // Filtrer les classes par le niveau actuel (pour SequencesCommunes)
  const filteredClassesByNiveau = useMemo(() => {
    if (!classes || !selectedNiveau) return [];
    return classes.filter((classe) =>
      classe.nom.toLowerCase().startsWith(selectedNiveau[0].toLowerCase())
    );
  }, [classes, selectedNiveau]);

  // Récupérer les activités parallèles groupées par séquence selon la
  // correspondance des dates. Quand les rails par période sont actifs, les
  // AUTOMATISME et RESOLUTION y vivent (comme côté niveau) : seuls les
  // QFlash restent en badge de séquence.
  const activitesParalleles = useMemo(() => {
    if (!filteredSequences) return {};
    const activites = {};

    filteredSequences
      .filter(seq => seq.type !== 'SEQUENCE' && !(railActif && (seq.type === 'AUTOMATISME' || seq.type === 'RESOLUTION')))
      .forEach(act => {
        // Trouver la séquence correspondante par date
        const matchingSeq = sequences.find(seq => {
          const actStart = new Date(act.start);
          const seqStart = new Date(seq.start);
          const seqEnd = new Date(seq.end);
          return actStart >= seqStart && actStart <= seqEnd;
        });

        if (matchingSeq) {
          if (!activites[matchingSeq.id]) {
            activites[matchingSeq.id] = [];
          }
          activites[matchingSeq.id].push(act);
        }
      });

    return activites;
  }, [filteredSequences, sequences, railActif]);

  // ── Les deux fils conducteurs, en lecture : rituels d'automatismes
  // (rail gauche) et résolutions de problèmes (rail droit) ──
  const rituels = useMemo(() => {
    if (!railActif || !filteredSequences) return [];
    return filteredSequences
      .filter((seq) => seq.type === 'AUTOMATISME')
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [railActif, filteredSequences]);

  const resolutions = useMemo(() => {
    if (!railActif || !filteredSequences) return [];
    return filteredSequences
      .filter((seq) => seq.type === 'RESOLUTION')
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [railActif, filteredSequences]);

  // Sections de frise par période, rattachement par DATE : même règle que
  // RoadMapNiveau (un élément daté pendant des vacances rejoint la période
  // suivante). null → repli sur la frise historique.
  const sections = useMemo(() => {
    if (!railActif || sequences.length === 0) return null;
    const secs = periodes.map((p) => ({ periode: p, sequences: [], rituels: [], resolutions: [], vacancesApres: null }));
    const attacher = (dateStr) => {
      const d = new Date(dateStr);
      let idx = periodes.findIndex((p) => d >= new Date(p.date_debut) && d < new Date(p.date_fin));
      if (idx === -1) idx = periodes.findIndex((p) => new Date(p.date_debut) >= d);
      if (idx === -1) idx = periodes.length - 1;
      return idx;
    };
    sequences.forEach((sequence, index) => {
      secs[attacher(sequence.start)].sequences.push({ sequence, index });
    });
    rituels.forEach((r) => { secs[attacher(r.start)].rituels.push(r); });
    resolutions.forEach((r) => { secs[attacher(r.start)].resolutions.push(r); });
    secs.forEach((sec, i) => {
      if (i === secs.length - 1) return;
      const fin = new Date(sec.periode.date_fin).getTime();
      const debutSuivant = new Date(periodes[i + 1].date_debut);
      sec.vacancesApres = vacancesScolaires.find((v) => {
        if (!v.title || !v.title.includes('Vacances')) return false;
        const dv = new Date(v.start);
        return dv.getTime() >= fin - 24 * 3600 * 1000 && dv <= debutSuivant;
      }) || null;
    });
    return secs;
  }, [railActif, periodes, sequences, rituels, resolutions, vacancesScolaires]);

  // Fonction pour vérifier si une séquence est "vue" pour la classe actuelle
  // Simple : on regarde si classeId est dans le tableau classes de la séquence
  const isSequenceVue = useCallback((sequence) => {
    if (!classeId || !sequence.classes) return false;
    return sequence.classes.includes(classeId);
  }, [classeId]);

  // Calculer les stats basées sur "vu/pas vu"
  const stats = useMemo(() => {
    const completed = sequences.filter(seq => isSequenceVue(seq)).length;
    const locked = sequences.filter(seq => !isSequenceVue(seq)).length;
    return { completed, locked };
  }, [sequences, isSequenceVue]);

  // Ouvrir le modal de liste des activités
  const handleViewActivities = (type, activities, sequenceTitle) => {
    setActivityListModalData({ type, activities, sequenceTitle });
    setIsActivityListModalOpen(true);
  };

  // Fermer le modal de liste des activités
  const handleCloseActivityList = () => {
    setIsActivityListModalOpen(false);
    setActivityListModalData({ type: null, activities: [], sequenceTitle: '' });
  };

  // Fonction pour basculer le statut "vu" d'une séquence
  const handleToggleVu = useCallback(async (sequenceId, newVuStatus) => {
    if (!classeId) return;

    setUpdatingSequenceId(sequenceId);

    const progressions = {
      niveau: selectedNiveau,
      add: newVuStatus ? [{ sequenceId, classeId }] : [],
      remove: !newVuStatus ? [{ sequenceId, classeId }] : []
    };

    try {
      await doPost(progressions);
      handleReload && handleReload();
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      alert("Erreur lors de la mise à jour du statut");
    } finally {
      setUpdatingSequenceId(null);
    }
  }, [classeId, selectedNiveau, doPost, handleReload]);

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-sky-50 via-blue-100 to-cyan-100 text-gray-800 overflow-x-hidden">
      {/* Background avec blobs liquid morphing style CardClasse */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Blobs SVG animés avec morphing */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="classe-blob1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a5f3fc" stopOpacity="0.5" />
              <stop offset="50%" stopColor="#67e8f9" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.2" />
            </linearGradient>
            <linearGradient id="classe-blob2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#bae6fd" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#7dd3fc" stopOpacity="0.25" />
            </linearGradient>
            <linearGradient id="classe-blob3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#e0f2fe" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#bae6fd" stopOpacity="0.2" />
            </linearGradient>
            <linearGradient id="classe-blob4" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.15" />
            </linearGradient>
            <radialGradient id="classe-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#e0f2fe" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#e0f2fe" stopOpacity="0" />
            </radialGradient>
          </defs>

          <path fill="url(#classe-blob1)">
            <animate attributeName="d" dur="8s" repeatCount="indefinite"
              values="M5,20 Q15,5 30,15 Q45,25 40,40 Q35,55 20,50 Q5,45 5,20;
                M10,15 Q25,0 35,12 Q45,24 42,42 Q39,60 22,52 Q5,44 10,15;
                M8,22 Q18,8 32,18 Q46,28 42,45 Q38,62 23,52 Q8,42 8,22;
                M5,20 Q15,5 30,15 Q45,25 40,40 Q35,55 20,50 Q5,45 5,20" />
          </path>
          <path fill="url(#classe-blob2)">
            <animate attributeName="d" dur="10s" repeatCount="indefinite"
              values="M70,10 Q90,15 92,35 Q94,55 80,50 Q66,45 68,25 Q70,5 70,10;
                M75,8 Q95,12 95,32 Q95,52 82,48 Q69,44 70,24 Q71,4 75,8;
                M68,12 Q88,18 90,38 Q92,58 78,52 Q64,46 66,26 Q68,6 68,12;
                M70,10 Q90,15 92,35 Q94,55 80,50 Q66,45 68,25 Q70,5 70,10" />
          </path>
          <path fill="url(#classe-blob3)">
            <animate attributeName="d" dur="7s" repeatCount="indefinite"
              values="M35,60 Q50,55 60,68 Q70,81 55,88 Q40,95 32,82 Q24,69 35,60;
                M38,58 Q55,52 65,65 Q75,78 58,86 Q41,94 34,80 Q27,66 38,58;
                M33,62 Q48,58 58,70 Q68,82 52,90 Q36,98 30,84 Q24,70 33,62;
                M35,60 Q50,55 60,68 Q70,81 55,88 Q40,95 32,82 Q24,69 35,60" />
          </path>
          <path fill="url(#classe-blob4)">
            <animate attributeName="d" dur="9s" repeatCount="indefinite"
              values="M0,55 Q10,50 20,58 Q30,66 25,80 Q20,94 8,88 Q-4,82 0,55;
                M0,52 Q12,46 24,55 Q36,64 30,78 Q24,92 10,86 Q-4,80 0,52;
                M0,58 Q8,54 18,62 Q28,70 22,84 Q16,98 6,90 Q-4,82 0,58;
                M0,55 Q10,50 20,58 Q30,66 25,80 Q20,94 8,88 Q-4,82 0,55" />
          </path>
          <path fill="url(#classe-blob1)">
            <animate attributeName="d" dur="11s" repeatCount="indefinite"
              values="M85,0 Q100,5 98,20 Q96,35 85,32 Q74,29 78,14 Q82,-1 85,0;
                M88,2 Q102,8 100,22 Q98,36 86,34 Q74,32 79,16 Q84,0 88,2;
                M83,0 Q98,4 96,18 Q94,32 82,30 Q70,28 76,12 Q82,-4 83,0;
                M85,0 Q100,5 98,20 Q96,35 85,32 Q74,29 78,14 Q82,-1 85,0" />
          </path>

          <circle cx="15" cy="40" r="1.5" fill="#67e8f9" opacity="0.6">
            <animate attributeName="cy" dur="4s" values="40;35;40" repeatCount="indefinite" />
          </circle>
          <circle cx="75" cy="45" r="1.2" fill="#a5f3fc" opacity="0.5">
            <animate attributeName="cy" dur="3.5s" values="45;40;45" repeatCount="indefinite" />
          </circle>
          <circle cx="90" cy="30" r="0.8" fill="#67e8f9" opacity="0.4">
            <animate attributeName="cy" dur="3s" values="30;26;30" repeatCount="indefinite" />
          </circle>

          <ellipse cx="50" cy="50" rx="35" ry="25" fill="url(#classe-glow)">
            <animate attributeName="rx" dur="6s" values="35;40;35" repeatCount="indefinite" />
            <animate attributeName="ry" dur="6s" values="25;30;25" repeatCount="indefinite" />
          </ellipse>
        </svg>

        {/* Grands cercles flous pour effet de profondeur */}
        <div className="absolute top-20 left-1/4 w-72 h-72 bg-cyan-100/40 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-40 right-1/4 w-64 h-64 bg-sky-100/50 rounded-full blur-3xl animate-float-delayed" />
        <div className="absolute top-1/2 left-10 w-56 h-56 bg-blue-100/40 rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-10 w-48 h-48 bg-cyan-50/60 rounded-full blur-3xl animate-float" />

        {/* Grid pattern subtil */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #0ea5e9 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      {/* Header — max-w aligné sur la frise (élargie pour les deux rails) */}
      <header className="relative z-20 pt-4 sm:pt-6 md:pt-10 pb-4 sm:pb-6 px-3 sm:px-4 md:px-8">
        <div className="max-w-[1400px] mx-auto">
          {/* Titre */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/70 backdrop-blur-md rounded-lg sm:rounded-xl flex items-center justify-center border border-sky-200 shadow-lg flex-shrink-0">
                <GraduationCap className="w-5 h-5 sm:w-7 sm:h-7 text-blue-600" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-extrabold text-slate-800 leading-tight flex items-center gap-1 sm:gap-2 flex-wrap">
                  <span className="truncate">Programmation {classeNom}</span>
                </h1>
                <p className="text-sm text-slate-500">
                  Niveau {selectedNiveau} · Mode consultation
                </p>
              </div>
            </div>

            {/* Onglets de navigation */}
            <div className="flex items-center bg-white/70 backdrop-blur-md rounded-xl sm:rounded-2xl border border-sky-200 p-1 shadow-lg">
              <button
                onClick={() => onChangeView && onChangeView("planning")}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 ${
                  selectedView === 'planning'
                    ? 'bg-gradient-to-r from-blue-500 to-sky-500 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-blue-50'
                }`}
              >
                <CalendarDays className="w-4 h-4" />
                <p> Planning </p> 
              </button>
              <button
                onClick={() => onChangeView && onChangeView("progress")}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 ${
                  selectedView === 'progress'
                    ? 'bg-gradient-to-r from-blue-500 to-sky-500 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-blue-50'
                }`}
              >
                <Users className="w-4 h-4" />
                <p> Equipe </p> 
              </button>
            </div>
          </div>

          {/* Stats rapides */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4">
            <div className="p-2.5 sm:p-3 md:p-4 bg-white/70 backdrop-blur-md rounded-xl sm:rounded-2xl border border-sky-200 shadow-lg">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                <Eye className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500" />
                <span className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider">Vues</span>
              </div>
              <p className="text-xl sm:text-2xl md:text-3xl font-black text-emerald-600">{stats.completed}</p>
            </div>
            <div className="p-2.5 sm:p-3 md:p-4 bg-white/70 backdrop-blur-md rounded-xl sm:rounded-2xl border border-sky-200 shadow-lg">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                <EyeOff className="w-3 h-3 sm:w-4 sm:h-4 text-orange-500" />
                <span className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider">À voir</span>
              </div>
              <p className="text-xl sm:text-2xl md:text-3xl font-black text-orange-500">{stats.locked}</p>
            </div>
            <div className="p-2.5 sm:p-3 md:p-4 bg-white/70 backdrop-blur-md rounded-xl sm:rounded-2xl border border-sky-200 shadow-lg">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                <span className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider">📈 Total</span>
              </div>
              <p className="text-xl sm:text-2xl md:text-3xl font-black text-blue-600">{sequences.length}</p>
            </div>
          </div>
             {/* Légende activités */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 md:gap-6 mt-3 sm:mt-4 md:mt-6 justify-center">
                <span className="text-[10px] sm:text-xs text-gray-600 uppercase tracking-wider font-semibold hidden sm:inline">Activités :</span>
                <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-emerald-800 font-bold bg-emerald-100 px-2 py-1 rounded-lg">
                  <span>⚡</span> <span className="hidden xs:inline">Automatismes</span><span className="xs:hidden">Auto.</span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-rose-800 font-bold bg-rose-100 px-2 py-1 rounded-lg">
                  <span>🧩</span> <span className="hidden xs:inline">Résolutions</span><span className="xs:hidden">Résol.</span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-amber-800 font-bold bg-amber-100 px-2 py-1 rounded-lg">
                  <span>💡</span> QFlash
                </div>
              </div>
        </div>
      </header>

      {/* Timeline - visible uniquement en mode planning */}
      {selectedView === 'planning' && (
        <main className="relative z-10 px-3 sm:px-4 md:px-8 pb-10 sm:pb-16 md:pb-20">
          <div className="max-w-[1400px] mx-auto">
            {/* Séquences */}
            {sequences.length > 0 ? (
              sections ? (
                /* ── Frise par PÉRIODES, même moule que la page niveau :
                   rails de CONSULTATION (pas d'ajout, pas de corbeille),
                   automatismes à gauche, résolutions à droite ── */
                <div className="py-6 sm:py-8 md:py-12">
                  {sections.map((sec) => (
                    <React.Fragment key={`periode-${sec.periode.numero}`}>
                      <div className="lg:grid lg:gap-4 lg:grid-cols-[200px_minmax(0,1fr)_200px] xl:gap-5 xl:grid-cols-[225px_minmax(0,1fr)_225px]">
                        {/* Rail desktop gauche : rituels d'automatismes */}
                        <div className="hidden lg:block">
                          <RailAutomatismes
                            periode={sec.periode}
                            rituels={sec.rituels}
                            onOpen={setContenusActifs}
                          />
                        </div>
                        <div>
                          {/* Bandeaux mobiles (masqués si vides en lecture) */}
                          <div className="lg:hidden">
                            <BandeauAutomatismes
                              periode={sec.periode}
                              rituels={sec.rituels}
                              onOpen={setContenusActifs}
                            />
                            <BandeauAutomatismes
                              type="RESOLUTION"
                              periode={sec.periode}
                              rituels={sec.resolutions}
                              onOpen={setContenusActifs}
                            />
                          </div>
                          <div className="relative space-y-6 sm:space-y-10 md:space-y-12">
                            {/* Ligne verticale de la section */}
                            <div className="hidden sm:block absolute left-1/2 top-0 bottom-0 w-0.5 sm:w-1 -translate-x-1/2">
                              <div className="absolute inset-0 bg-gradient-to-b from-blue-400/60 via-sky-400/40 to-cyan-300/20 rounded-full" />
                            </div>
                            {sec.sequences.map(({ sequence, index }) => (
                              <SequenceCardConsultation
                                key={sequence.id}
                                sequence={sequence}
                                index={index}
                                isVu={isSequenceVue(sequence)}
                                onToggleVu={handleToggleVu}
                                isLoading={updatingSequenceId === sequence.id}
                                activitesParalleles={activitesParalleles[sequence.id]}
                                onViewActivities={handleViewActivities}
                                onViewContenus={setContenusActifs}
                                classeDetail={classeDetail}
                              />
                            ))}
                          </div>
                        </div>
                        {/* Rail desktop droit : résolutions de problèmes */}
                        <div className="hidden lg:block">
                          <RailAutomatismes
                            type="RESOLUTION"
                            periode={sec.periode}
                            rituels={sec.resolutions}
                            onOpen={setContenusActifs}
                          />
                        </div>
                      </div>
                      {sec.vacancesApres ? (
                        <div className="my-8 sm:my-10">
                          <VacancesSeparator
                            title={sec.vacancesApres.title}
                            start={sec.vacancesApres.start}
                            end={sec.vacancesApres.end}
                          />
                        </div>
                      ) : null}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                /* ── Repli historique (périodes indisponibles) ── */
                <div className="relative space-y-6 sm:space-y-10 md:space-y-12 py-6 sm:py-8 md:py-12">
                  {/* Ligne verticale - cachée sur mobile */}
                  <div className="hidden sm:block absolute left-1/2 top-0 bottom-0 w-0.5 sm:w-1 -translate-x-1/2">
                    <div className="absolute inset-0 bg-gradient-to-b from-blue-400/60 via-sky-400/40 to-cyan-300/20 rounded-full" />
                  </div>
                  {sequences.map((sequence, index) => (
                    <SequenceCardConsultation
                      key={sequence.id}
                      sequence={sequence}
                      index={index}
                      isVu={isSequenceVue(sequence)}
                      onToggleVu={handleToggleVu}
                      isLoading={updatingSequenceId === sequence.id}
                      activitesParalleles={activitesParalleles[sequence.id]}
                      onViewActivities={handleViewActivities}
                      onViewContenus={setContenusActifs}
                      classeDetail={classeDetail}
                    />
                  ))}
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center py-12 sm:py-16 md:py-20">
                <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-white/80 backdrop-blur-md rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-lg border border-sky-200">
                  <span className="text-2xl sm:text-3xl md:text-4xl">📚</span>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">Aucune séquence</h3>
                <p className="text-gray-600 text-center max-w-md text-sm sm:text-base px-4">
                  Il n'y a pas encore de séquence créée pour ce niveau.
                  Contactez le coordonnateur de discipline pour créer la progression commune.
                </p>
              </div>
            )}

            {/* Fin d'année marker */}
            {sequences.length > 0 && (
              <div className="relative flex justify-center mt-8 sm:mt-10 md:mt-12">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center shadow-2xl shadow-emerald-500/40 border-2 sm:border-4 border-white/50">
                  <span className="text-xl sm:text-2xl">🎓</span>
                </div>
              </div>
            )}
          </div>
        </main>
      )}

      {/* Suivi des progrès - visible uniquement en mode progress */}
      {selectedView === 'progress' && (
        <div className="px-3 sm:px-4 md:px-8 pb-10">
          <div className="max-w-[1400px] mx-auto">
            <SequencesCommunes
              sequenceProf={filteredSequences}
              filteredClasses={filteredClassesByNiveau}
              niveau={selectedNiveau}
            />
          </div>
        </div>
      )}
   
      {/* Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-20px) scale(1.05); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(20px) scale(1.02); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 10s ease-in-out infinite;
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>

      {/* Modal Liste des Activités (consultation seule - pas de bouton supprimer) */}
      <ActivityListModal
        isOpen={isActivityListModalOpen}
        onClose={handleCloseActivityList}
        type={activityListModalData.type}
        activities={activityListModalData.activities}
        sequenceTitle={activityListModalData.sequenceTitle}
      />

      {/* Panneau détail partagé (pill séquence chargée / chip d'un fil) —
          consultation : pas d'onAddSavoir, donc pas de pied d'édition ;
          statsPour ajoute l'acquisition de la classe sur chaque contenu */}
      <PanneauContenus
        isOpen={contenusActifs !== null}
        activite={contenusActifs}
        rubriques={rubriqueParAttendu}
        onClose={() => setContenusActifs(null)}
        statsPour={(item, type) => calculateAcquisitionStats(item, type, classeDetail?.eleves || [])}
      />
    </div>
  );
}

export default RoadMapClasse;
